import type { AttemptType, RankingScore, ScoreCard } from '../services/round-scores';

/**
 * One card in the shorthand every board uses: `T4 Z2` a top on the fourth go
 * with the zone on the second, `Z2` the zone on the second and no top, `|3`
 * three tries and nothing to show for them.
 *
 * **A topped card always names its zone too** (#363): whoever held the top went
 * past the zone, and the go they reached it on is what the ranking breaks ties
 * on. It is the zone marked before the top, or else the top's own go — `T1 Z1`,
 * `T9 Z9` — the same reading as the server's `BoulderRanking.goes`.
 *
 * It lives apart from the components that draw it because the panel's dialog
 * and the member app's cards read a card the same way, and neither owns the
 * rule.
 */
export function summarise(attempts: AttemptType[]): string {
  if (attempts.length === 0) return '-';

  const top = attempts.indexOf('top');
  const marked = attempts.indexOf('zone');

  if (top >= 0) {
    const zone = marked >= 0 && marked < top ? marked : top;
    return `T${top + 1} Z${zone + 1}`;
  }

  if (marked >= 0) return `Z${marked + 1}`;

  return `|${attempts.length}`;
}

/**
 * A boulder round at a glance: how many problems a person topped and zoned, and
 * how many of each were on a judged block — the share the "solved" cell shows
 * under a regulation that tells judged blocks apart (`3 Top (1G) · 2 Zone (1G)`).
 */
export interface RoundTally {
  tops: number;
  zones: number;
  judgedTops: number;
  judgedZones: number;
}

/**
 * The tops and zones on a whole card set, counted the way the ranking counts
 * them: **a top is a zone too** (#363), so a topped problem adds one to each
 * and the zones are never fewer than the tops; a problem with two zones and no
 * top is a single zone. The judged share is counted the same way, over the
 * problems in `judged` (their round climb ids).
 *
 * **Display only, and decides no place.** The places are the server's, and on a
 * placed row these are the same numbers as its `score` — this is what draws the
 * rows that carry none (a narrowed board, somebody not placed yet). Only
 * meaningful for boulder rounds — a lead card carries none of these marks.
 */
export function tally(cards: ScoreCard[], judged: ReadonlySet<string> = new Set()): RoundTally {
  const counts: RoundTally = { tops: 0, zones: 0, judgedTops: 0, judgedZones: 0 };

  for (const card of cards) {
    const types = card.attempts.map((attempt) => attempt.attempt_type);
    const onJudged = judged.has(card.round_climb_id);
    const topped = types.includes('top');

    if (topped) {
      counts.tops += 1;
      if (onJudged) counts.judgedTops += 1;
    }
    if (topped || types.includes('zone')) {
      counts.zones += 1;
      if (onJudged) counts.judgedZones += 1;
    }
  }

  return counts;
}

/**
 * The two attempt counts a boulder place is broken on, **as the server's
 * ranking counted them** — read off a row's `score`, never worked out here: the
 * goes to the tops, and the goes to the zones (a top counting as a zone, its
 * own go when the zone was not marked before it). Under OPES, judged blocks
 * only. Null for a row with no score (not placed, or a lead round).
 */
export function rankingAttempts(
  score: RankingScore | null | undefined
): { top: number; zone: number } | null {
  if (!score || !('top_attempts' in score)) return null;
  return { top: score.top_attempts, zone: score.zone_attempts };
}

/** The ids of the judged problems among `climbs` — what `tally` counts a share of. */
export function judgedIds(climbs: { id: string; requires_judge?: boolean }[]): Set<string> {
  return new Set(climbs.filter((climb) => climb.requires_judge).map((climb) => climb.id));
}
